import {Module} from '@mamdev/cad.static-next-lib';
import pubSub from '@mamdev/cad.static-next-lib/js/services/pubsub';
import Viewport from '../../ts/services/viewport';
import Tracking2 from '../../ts/services/tracking2';

const EXPAND_TRANSITION_DURATION = 500;

export default class ImageGallery extends Module {
  preloadLinks: HTMLLinkElement[];

  // Time that the slide must be intersecting the viewport before sending a PI, to avoid sending too many PIs when scrolling fast.
  trackingDelay: number;

  // Used to detect scroll direction.
  lastScrollTop: number;
  slidesToShow: number;
  scrollDirection: 'up' | 'down' | undefined;

  slideBottomObserver: IntersectionObserver | null;
  slideTopObserver: IntersectionObserver | null;
  slideshowEndObserver: IntersectionObserver | null;

  slideCountWithoutAds: number;

  inAppView: boolean;

  PIURL: string;

  hasBreakingNews: boolean;

  pageContentElement: HTMLElement | null;

  expanded: boolean;

  static attachTo(root: HTMLElement): ImageGallery {
    return new ImageGallery(root);
  }

  constructor(root: HTMLElement) {
    super(root);

    this.name_ = 'imagegallery';

    this.preloadLinks = [];

    this.trackingDelay = 200;
    this.lastScrollTop = 0;
    this.scrollDirection = undefined;

    this.slideBottomObserver = null;
    this.slideTopObserver = null;
    this.slideshowEndObserver = null;
    this.slidesToShow = 3;

    this.PIURL = window.ui.brain.piUrl || '';
    this.inAppView = Viewport.isInAppView();
    this.expanded = false;
  }

  start() {
    try {
      this.log('start');

      this.hasBreakingNews = Viewport.isDesktop() && document.querySelector('.mod-breakingnews-sticky') !== null;

      const slides = this.root_.getElementsByClassName('imagegallery-slide');

      this.slideCountWithoutAds = Array.from(slides).filter(s => !s.classList.contains('co-slide')).length;

      const headArrow = document.getElementsByClassName('gallery-page__head-arrow')[0];

      if (headArrow) {
        headArrow.addEventListener('click', e => {
          e.preventDefault();

          const galleryInfo = document.querySelector('.gallery-page__info');

          if (galleryInfo) this.scrollTo(galleryInfo);
        });
      }

      this.root_.querySelectorAll('.imagegallery-slide__next-slide').forEach(el => {
        el.addEventListener('click', e => this.showNextSlide(e));
      });

      this.root_.querySelectorAll('.imagegallery-slide__icon-expand').forEach(el => {
        el.addEventListener('click', () => this.toggleExpandedView(el.closest('.imagegallery-slide')));
      });

      this.subscribe('content:scrollToGallery', () => {
        const firstSlide = this.root_.getElementsByClassName('imagegallery-slide')[0];

        if (firstSlide) {
          this.scrollTo(firstSlide);
        }
      });

      this.setupObservers();
      this.preloadSlide(0);
      this.preloadSlide(1);

      // load image which is in the viewvp port (page reload somewhere else instead of top of the page)
      window.setTimeout(() => {
        this.root_.querySelectorAll('.imagegallery-slide').forEach(el => {
          const slide = el as HTMLElement;
          if (slide && Viewport.isElementInViewport(slide, 500)) {
            this.imageLoadingCSS(slide);
          }
        });
      }, 1000);
      this.startScrollDetection();
    } catch (e) {
      console.error(e);
    }
  }

  fadeOutOrInSlide(slide: HTMLElement, effect: string) {
    if (effect === 'add') {
      slide.getElementsByClassName('imagegallery-slide__picture')[0].classList.add('masked');
      (slide.getElementsByClassName('imagegallery-slide__content')[0] as HTMLElement).style.display = 'none';
    } else {
      slide.getElementsByClassName('imagegallery-slide__picture')[0].classList.remove('masked');
      (slide.getElementsByClassName('imagegallery-slide__content')[0] as HTMLElement).style.display = 'block';
    }
  }

  imageLoadingCSS(slide: HTMLElement) {
    const pictureElement = slide.getElementsByClassName('imagegallery-slide__picture')[0];
    const imgElement = slide.getElementsByTagName('img')[0];

    if (pictureElement && imgElement && !imgElement.classList.contains('loaded')) {
      const backgroundContainer = pictureElement.getElementsByClassName('imagegallery-slide__background')[0] as HTMLElement;
      console.log('setting background', backgroundContainer, backgroundContainer.dataset.slideBackground);
      if (backgroundContainer?.dataset.slideBackground) {
        backgroundContainer.style.backgroundImage = `url(${backgroundContainer.dataset.slideBackground})`;
      }

      imgElement.classList.remove('not-loaded');
      imgElement.classList.add('loaded');
      backgroundContainer.classList.add('loaded');
    }
  }

  preloadSlide(index: number) {
    try {
      const slideContainer = this.root_;
      const slide = slideContainer.children.item(index) as HTMLElement;

      if (!slide) return;
      if (slide.dataset.preloaded) return;
      if (slide.classList.contains('co-slide')) return; // Avoid requesting ads twice.

      this.imageLoadingCSS(slide);

      slide.dataset.preloaded = '1';

      this.log('slide ' + index + ' preloaded');
    } catch (e) {
      console.error(e);
    }
  }

  /**
   * Start detecting the scroll direction.
   */
  startScrollDetection() {
    window.addEventListener('scroll', () => {
      if (document.documentElement.scrollTop > this.lastScrollTop) {
        this.scrollDirection = 'down';
      } else {
        this.scrollDirection = 'up';
      }

      this.lastScrollTop = document.documentElement.scrollTop;
    });
  }

  /**
   * Setup intersection observers on each slide.
   * If the end of a slide has been reached, send PI and preload the next slide(s).
   */
  setupObservers() {
    try {
      let lastSlideSeen: HTMLElement;
      let lastSlideTracked: HTMLElement;

      const slideshowContent = this.root_;

      // Observe when the content of a slide comes into view while scrolling down. For PI purposes, the user must reach the end of the slide,
      // so the slide content (textual part for normal slide and the whole ad for ad slides) is taken into account.
      this.slideBottomObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          // Make sure the entry is actually intersecting (sometimes IntersectionObserver fires the callback even if no intersection occurred).
          if (!entry.isIntersecting) return;
          if (this.scrollDirection !== 'down') return;

          const element = entry.target;
          const slide = element.closest<HTMLElement>('.imagegallery-slide');

          if (slide) {
            const slideIndex = parseInt(slide.dataset.slideIndex ?? '');

            lastSlideSeen = slide;

            // Avoid tracking multiple times the same slide.
            if (lastSlideTracked === slide) return;

            this.log('slide ' + slideIndex + ' seen bottom scroll');

            // Preload the next two slides.
            this.preloadSlide(slideIndex);
            this.preloadSlide(slideIndex + 1);
            this.preloadSlide(slideIndex + 2);

            // Delay sending the pixel to avoid sending pixels when the user scrolls too fast.
            // We only want to send a pixel only for slides the user actually looked at (ideally only the last one), not just skimmed.
            // TODO: switch to "scrollend" event once it becomes supported.
            setTimeout(slideElement => {
            // Only fire a PI if the user is on the last seen slide.
              if (slideElement === lastSlideSeen) {
                this.sendSlidePI(slideElement);
                lastSlideTracked = slideElement;
              }
            }, this.trackingDelay, slide);
          }
        });
      }, {
        root: null,
        rootMargin: '0px',
        threshold: 0.9
      });

      const slideCaptions = slideshowContent.getElementsByClassName('imagegallery-slide__content');

      Array.from(slideCaptions).forEach(caption => {
        this.slideBottomObserver?.observe(caption);
      });

      // Observe slide intersections when scrolling up. A PI will be fired when the top of the slide comes into the viewport.
      this.slideTopObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          // Make sure the entry is actually intersecting (sometimes IntersectionObserver fires the callback even if no intersection occurred).
          if (!entry.isIntersecting) return;
          if (this.scrollDirection !== 'up') return;

          const element = entry.target;
          const slide = element.closest<HTMLElement>('.imagegallery-slide');

          if (slide) {
            const slideIndex = parseInt(slide.dataset.slideIndex ?? '');

            lastSlideSeen = slide;

            // Avoid tracking multiple times the same slide.
            if (lastSlideTracked === slide) return;

            this.log('slide ' + slideIndex + ' seen up scroll');

            // Preload the next two slides.
            this.preloadSlide(slideIndex);
            this.preloadSlide(slideIndex - 1);
            this.preloadSlide(slideIndex - 2);

            // Delay sending the pixel to avoid sending pixels when the user scrolls too fast.
            // We only want to send a pixel only for slides the user actually looked at (ideally only the last one), not just skimmed.
            // TODO: switch to "scrollend" event once it becomes supported.
            setTimeout(slideElement => {
            // Only fire a PI if the user is on the last seen slide.
              if (slideElement === lastSlideSeen) {
                this.sendSlidePI(slideElement);
                lastSlideTracked = slideElement;
              }
            }, this.trackingDelay, slide);
          }
        });
      }, {
        root: null,
        rootMargin: '0px',
        threshold: 0.9
      });

      const slideTops = slideshowContent.getElementsByClassName('imagegallery-slide__picture');

      Array.from(slideTops).forEach(topElement => {
        this.slideTopObserver?.observe(topElement);
      });
    } catch (e) {
      console.error(e);
    }
  }

  // if expand don't refresh ALL ads, just the right column
  refreshAds(galleryMode: string) {
    console.log('set ad refresh here ' + galleryMode);

    if (galleryMode === 'expanded') {
      pubSub.publish('ad:refreshAdSky');
    } else {
      pubSub.publish('ad:refreshAll');
    }
  }

  /**
   * Send navigation PI and restart welcome back timer.
   */
  sendSlidePI(slide: HTMLElement) {
    if (!this.PIURL) {
      this.log('No PI URL set');
      return;
    }

    this.log('send PI for slide ' + slide.dataset.slideNumber + ' ' + (this.scrollDirection === 'up' ? 'prev_slide' : 'next_slide'));

    const isAd = slide.classList.contains('co-slide');

    if (isAd) {
      pubSub.publish('slideshow:setAdPixel', {
        piUrl: this.PIURL
      });
    } else {
      let currentClickname;

      if (this.scrollDirection === 'down') {
        currentClickname = this.expanded ? 'slideshow_right_magnification' : 'slideshow_right';
      } else if (this.scrollDirection === 'up') {
        currentClickname = this.expanded ? 'slideshow_left_magnification' : 'slideshow_left';
      } else {
        currentClickname = 'slideshow_none';
      }

      const currentSlidenumber = slide.dataset.slideNumber + '/' + this.slideCountWithoutAds;
      window.utag_data.subContentIndex = slide.dataset.slideNumber;

      pubSub.publish('slideshow:setPixel', {
        piUrl: this.PIURL,
        click1: 'undefined',
        click2: 'undefined',
        click3: 'undefined',
        click4: 'undefined',
        referrer: 'undefined',
        slidenumber: encodeURIComponent(currentSlidenumber),
        clickname: currentClickname
      });

      // CADNPCA-9434 send completion rate
      const compRate = Number(slide.dataset.slideNumber) / this.slideCountWithoutAds;
      pubSub.publish('content:recoCCR', {progress: compRate, contentType: window.ui.recom?.contentType});
    }

    pubSub.publish('welcomeback:restartWBTimer');
  }

  toggleExpandedView(slide: HTMLElement | null) {
    document.documentElement.classList.toggle('expanded-view');

    Tracking2.callTealiumView({newContentType: 'slideshow.fullscreen-image'});

    if (document.documentElement.classList.contains('expanded-view')) {
      this.refreshAds('expanded');
      this.expanded = true;

      // sendPIPixel
      pubSub.publish('magnifier:setPixel', {
        referrer: '&referrer=undefined',
        click1: '&click1=' + window.ui.pageinfo.coremid,
        click2: '&click2=magnifier',
        click3: '&click3=open',
        click4: '&click4=1',
        clickname: '&clickname=magnifier_open'
      });
    } else {
      this.expanded = false;
      this.refreshAds('unexpanded');

      // sendPIPixel
      pubSub.publish('magnifier:setPixel', {
        referrer: '&referrer=undefined',
        click1: '&click1=' + window.ui.pageinfo.coremid,
        click2: '&click2=magnifier',
        click3: '&click3=close',
        click4: '&click4=1',
        clickname: '&clickname=magnifier_close'
      });
    }

    if (slide) {
      window.setTimeout(() => {
        this.scrollTo(slide);
      }, EXPAND_TRANSITION_DURATION);
    }
  }

  showNextSlide(e: Event) {
    e.preventDefault();

    if (!e.target) return;

    const eventTarget = e.target as HTMLElement;
    const currentSlide = eventTarget.closest<HTMLElement>('.imagegallery-slide');

    if (!currentSlide) return;

    let nextSlide;
    let trackingClickname = '';

    if (currentSlide.classList.contains('imagegallery__main-slide')) {
      nextSlide = eventTarget.closest('.imagegallery')?.getElementsByClassName('imagegallery-slide')[1] as HTMLElement;
      trackingClickname = 'arrow_0';
    } else {
      nextSlide = currentSlide.nextElementSibling as HTMLElement;

      // Specify tracking parameters.
      if (currentSlide.classList.contains('imagegallery-slide-info')) {
        trackingClickname = 'arrow_0t'; // info slide after the main slide
      } else if (currentSlide.dataset.coId) {
        trackingClickname = `arrow_ad_${currentSlide.dataset.coId}`; // ad slide
      } else {
        trackingClickname = `arrow_${currentSlide.dataset.slideNumber}`; // regular slide
      }
    }

    if (nextSlide) {
      // Ads on desktop view are hidden, so we can't scroll to an ad slide
      if (nextSlide.dataset.coId && Viewport.isDesktop()) {
        nextSlide = nextSlide.nextElementSibling;
      }

      if (nextSlide) {
        this.scrollTo(nextSlide);

        // Track arrow clicked.
        this.publish('slideshow:arrowClicked', {
          clickname: trackingClickname
        });
      }
    }
  }

  scrollTo(element: Element) {
    const topBarOffset = this.inAppView ? 0 : 50;
    const offset = this.hasBreakingNews ? topBarOffset + 40 : topBarOffset; // Account for fixed top bar and breaking news bar.

    Viewport.scrollToElement(element, offset);
  }
}
